import type {
  SelectionsValidateResponse,
  BrandCheck,
  BrandSetCheck,
  FilterCheck,
  SegmentCheck,
} from "@/interfaces/selections/selections.types";

export interface SelectionPartition {
  usable: {
    brandSets: BrandSetCheck[];
    segments: SegmentCheck[];
    brands: BrandCheck[];
    filters: FilterCheck[];
  };
  adoptable: {
    brandSets: BrandSetCheck[];
    segments: SegmentCheck[];
  };
  blocked: {
    brandSets: BrandSetCheck[];
    segments: SegmentCheck[];
    brands: BrandCheck[];
    filters: FilterCheck[];
  };
}

// OWNED/ACCESSIBLE/ALREADY_HAVE all mean "the caller already has a usable equivalent".
// Shared across brand sets and segments intentionally: ACCESSIBLE is a segment-only
// (system-segment) status that brand sets never emit, so sharing the set is safe.
const USABLE = new Set<string>(["OWNED", "ACCESSIBLE", "ALREADY_HAVE"]);

// A filter is usable only when it and every referenced option are accessible;
// any inaccessible option makes the whole filter something to surface, not apply.
const filterUsable = (f: FilterCheck): boolean =>
  f.accessible && (f.options ?? []).every((o) => o.accessible);

export function partitionSelections(
  response: SelectionsValidateResponse,
): SelectionPartition {
  const brandSets = response.brandSets ?? [];
  const segments = response.segments ?? [];
  const brands = response.brands ?? [];
  const filters = response.filters ?? [];

  return {
    usable: {
      brandSets: brandSets.filter((c) => USABLE.has(c.status)),
      segments: segments.filter((c) => USABLE.has(c.status)),
      brands: brands.filter((c) => c.accessible),
      filters: filters.filter(filterUsable),
    },
    adoptable: {
      brandSets: brandSets.filter((c) => c.status === "ELIGIBLE"),
      segments: segments.filter((c) => c.status === "ELIGIBLE"),
    },
    blocked: {
      brandSets: brandSets.filter((c) => c.status === "NOT_ELIGIBLE"),
      segments: segments.filter((c) => c.status === "NOT_ELIGIBLE"),
      brands: brands.filter((c) => !c.accessible),
      filters: filters.filter((c) => !filterUsable(c)),
    },
  };
}

export function hasAdoptable(p: SelectionPartition): boolean {
  return p.adoptable.brandSets.length > 0 || p.adoptable.segments.length > 0;
}

export function hasBlocked(p: SelectionPartition): boolean {
  return (
    p.blocked.brandSets.length > 0 ||
    p.blocked.segments.length > 0 ||
    p.blocked.brands.length > 0 ||
    p.blocked.filters.length > 0
  );
}
